import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, Package, ShieldCheck, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/i18n";
import { useAuth } from "@/lib/auth";
import { getOrders, type Order } from "@/lib/api";
import { formatPrice } from "@/lib/utils";

export default function Account() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { user, initialized, checkAuth, logout } = useAuth();
  const [orders, setOrders] = useState<Order[] | null>(null);
  const [loadingOrders, setLoadingOrders] = useState(true);

  useEffect(() => {
    if (!initialized) {
      checkAuth();
      return;
    }
    if (!user) {
      navigate("/login");
      return;
    }
    let active = true;
    setLoadingOrders(true);
    getOrders()
      .then((res) => {
        if (active) setOrders(res.orders);
      })
      .catch(() => {
        if (active) {
          setOrders([]);
          toast.error(t.common.error);
        }
      })
      .finally(() => {
        if (active) setLoadingOrders(false);
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialized, user, navigate, checkAuth]);

  if (!initialized) {
    return (
      <div className="page-shell">
        <div className="container">
          <p style={{ color: "var(--color-text-muted)" }}>{t.common.loading}</p>
        </div>
      </div>
    );
  }

  if (!user) return null;

  const isAdmin = user.role === "admin";

  async function handleLogout() {
    await logout();
    navigate("/");
  }

  return (
    <div className="page-shell">
      <div className="container max-w-4xl">
        <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
          <div>
            <span className="eyebrow">{t.account.profile}</span>
            <h1 className="text-4xl mb-3" style={{ fontFamily: "var(--font-display)", color: "var(--color-charcoal)" }}>
              {t.account.welcome}, {user.name || user.email.split("@")[0]}
            </h1>
            <p className="lede" style={{ color: "var(--color-text-muted)" }}>{t.account.subtitle}</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {isAdmin && (
              <Link to="/admin" className="btn-secondary flex items-center gap-2">
                <ShieldCheck size={16} />{t.account.adminPanel}
              </Link>
            )}
            <button onClick={() => void handleLogout()} className="btn-secondary flex items-center gap-2">
              <LogOut size={16} />{t.account.logout}
            </button>
          </div>
        </div>

        <section className="panel p-6 sm:p-8">
          <div className="grid gap-6 sm:grid-cols-3">
            <div>
              <p className="text-xs uppercase tracking-[0.25em]" style={{ color: "var(--color-text-light)" }}>{t.account.name}</p>
              <p className="mt-1" style={{ color: "var(--color-charcoal)" }}>{user.name || "—"}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.25em]" style={{ color: "var(--color-text-light)" }}>{t.account.email}</p>
              <p className="mt-1 break-all" style={{ color: "var(--color-charcoal)" }}>{user.email}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.25em]" style={{ color: "var(--color-text-light)" }}>{t.account.profile}</p>
              <p className="mt-1" style={{ color: "var(--color-terracotta)" }}>{isAdmin ? t.account.roleAdmin : t.account.roleUser}</p>
            </div>
          </div>
        </section>

        <section className="mt-12">
          <div className="mb-5 flex items-center gap-2">
            <Package size={18} />
            <h2 className="text-2xl" style={{ fontFamily: "var(--font-display)", color: "var(--color-charcoal)" }}>{t.account.orders}</h2>
          </div>

          {loadingOrders ? (
            <p className="border px-4 py-10 text-center text-sm" style={{ borderColor: "var(--color-border)", color: "var(--color-text-muted)" }}>{t.common.loading}</p>
          ) : orders && orders.length === 0 ? (
            <div className="border px-4 py-14 text-center" style={{ borderColor: "var(--color-border)" }}>
              <ShoppingBag size={30} className="mx-auto mb-4" style={{ color: "var(--color-text-light)" }} />
              <p style={{ color: "var(--color-text-muted)" }}>{t.account.ordersEmpty}</p>
              <Link to="/gallery" className="btn-primary mt-6 inline-flex items-center gap-2">
                {t.nav.shopAll}
              </Link>
            </div>
          ) : (
            <div className="space-y-3">
              {(orders || []).map((order) => (
                <article key={order.id} className="border bg-white p-5" style={{ borderColor: "var(--color-border)" }}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-medium" style={{ color: "var(--color-charcoal)" }}>
                        {t.account.orderNumber} #{order.id} · {formatPrice(order.total_amount, order.currency)}
                      </p>
                      <p className="mt-1 text-sm" style={{ color: "var(--color-text-muted)" }}>
                        {order.item_titles || "—"}
                      </p>
                    </div>
                    <div className="text-right">
                      <span className="border px-2 py-0.5 text-[10px] uppercase tracking-[0.18em]" style={{ borderColor: "var(--color-border)", color: "var(--color-text-light)" }}>
                        {order.status}
                      </span>
                      <p className="mt-1.5 text-xs" style={{ color: "var(--color-text-light)" }}>
                        {new Date(order.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}